import React, { useState, useEffect, useMemo } from 'react';
import { marked } from 'marked';
import { db } from '../../firebase';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import AccessNotice from '../../components/Portal/AccessNotice';
import { isPermissionDenied } from '../../utils/permissions';
import './Canon.css';

/**
 * Canon — a READ-ONLY view of the CYV canonical documents.
 *
 * WHY THIS PAGE EXISTS (2026-08-15). Canon lives in `docs/canon/` in this repo,
 * which is machine-local: reachable from a checkout and nowhere else. Agents on
 * other surfaces read it through the `read_canon` MCP tool. This page is the
 * HUMAN half of the same problem — Anthony reading canon in a browser, from any
 * machine, without asking Claude to read it to him.
 *
 * It renders the same Firestore mirror (`canonDocs`) that read_canon serves,
 * generated from the repo by `scripts/sync-canon.js`. THE MIRROR CAN LAG THE
 * REPO, so the sha and generated-at are shown in the header rather than buried:
 * a page that presents cached canon as current is the most misleading thing it
 * could do.
 *
 * OWNERS ONLY — Anthony and Grace, not staff (Anthony, 2026-08-15). Canon holds
 * worker classification, pay pointers, tuition economics and family billing
 * detail, i.e. the material core.md's own workspace-separation rule keeps in the
 * Owners private teamspace, which Annelise cannot reach. The Firestore rule
 * (`allow read: if isOwner()`) is the actual wall; the client check below only
 * exists so a non-owner sees a sentence instead of a permission error.
 *
 * NO EDITING, EVER, and that is not a missing feature. The repo is the source of
 * truth — a write here would be silently destroyed by the next sync, which is
 * why client writes are closed in the rules rather than merely unimplemented.
 */

// Rendered with `marked` + dangerouslySetInnerHTML, deliberately.
// ---------------------------------------------------------------------------
// The obvious alternative, MarkdownText from Work/cleanMarkdown.js, parses a
// safe SUBSET aimed at Notion-imported task bodies and HAS NO TABLE SUPPORT.
// core.md and STATE.md are full of tables, so that renderer would silently
// mangle the densest, most load-bearing parts of canon into loose text.
//
// The trust boundary is what makes the HTML path acceptable here, and it is
// narrow enough to state exactly: this content originates in a PRIVATE repo,
// is written to Firestore only by an Admin-SDK script, and the collection is
// write-closed to every client by rule. Injecting hostile markup would require
// commit access to the repo — at which point the attacker already has code
// execution and does not need an XSS. This is first-party content rendering
// first-party content; it is not user input.
marked.setOptions({ gfm: true, breaks: false });

const CANON_COLLECTION = 'canonDocs';
const META_ID = '__meta';
const DEFAULT_DOC = 'core.md';

/** Firestore doc ids cannot hold '/', so nested paths are flattened with '__'. */
const idFor = (p) => p.replace(/\.md$/, '').split('/').join('__');

const toJsDate = (val) => {
	if (!val) return null;
	if (typeof val.toDate === 'function') return val.toDate();
	if (val instanceof Date) return val;
	const d = new Date(val);
	return isNaN(d) ? null : d;
};

const Canon = () => {
	const { userProfile } = useAuth();
	const isOwner = userProfile?.owner === true;

	const [meta, setMeta] = useState(null);
	const [paths, setPaths] = useState([]);
	const [selected, setSelected] = useState(DEFAULT_DOC);
	const [docBody, setDocBody] = useState(null);
	const [loading, setLoading] = useState(true);
	const [bodyLoading, setBodyLoading] = useState(false);
	const [permDenied, setPermDenied] = useState(false);
	const [error, setError] = useState(null);

	// Listing. `__meta.paths` is the cheap path (one read); falling back to a
	// collection scan keeps the page working if a sync predates the meta doc.
	useEffect(() => {
		if (!isOwner) {
			setLoading(false);
			return;
		}
		let cancelled = false;
		(async () => {
			try {
				const metaSnap = await getDoc(doc(db, CANON_COLLECTION, META_ID));
				if (cancelled) return;
				if (metaSnap.exists()) {
					const m = metaSnap.data();
					setMeta(m);
					setPaths(m.paths || []);
				} else {
					const all = await getDocs(collection(db, CANON_COLLECTION));
					if (cancelled) return;
					setPaths(all.docs.filter((d) => d.id !== META_ID).map((d) => d.data().path).sort());
				}
			} catch (err) {
				if (cancelled) return;
				// A rules denial must not read as "canon is empty" (#27).
				if (isPermissionDenied(err)) setPermDenied(true);
				else setError(err.message);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [isOwner]);

	// Body of the selected document.
	useEffect(() => {
		if (!isOwner || !selected) return;
		let cancelled = false;
		setBodyLoading(true);
		(async () => {
			try {
				const snap = await getDoc(doc(db, CANON_COLLECTION, idFor(selected)));
				if (cancelled) return;
				setDocBody(snap.exists() ? snap.data() : null);
			} catch (err) {
				if (cancelled) return;
				if (isPermissionDenied(err)) setPermDenied(true);
				else setError(err.message);
			} finally {
				if (!cancelled) setBodyLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [selected, isOwner]);

	const html = useMemo(
		() => (docBody?.markdown ? marked.parse(docBody.markdown) : ''),
		[docBody]
	);

	// Both non-owner branches use the shared AccessNotice (it takes `what`, and
	// its own copy already says an owner can grant access) plus one line of
	// context, because "you don't have access to canon" invites a request that
	// should not be granted — canon is owners-only by design, not by oversight.
	if (!isOwner || permDenied) {
		return (
			<div className='canon-page'>
				<AccessNotice what='the canon documents' />
				<p className='canon-muted canon-centered'>
					Canon is limited to Grace and Anthony. It carries pay, tuition and family
					billing detail that sits outside the shared Work space.
				</p>
			</div>
		);
	}

	const generated = toJsDate(meta?.generatedAt);

	return (
		<div className='canon-page'>
			<header className='canon-head'>
				<div>
					<h1 className='canon-title'>Canon</h1>
					<p className='canon-sub'>
						Read-only mirror of <code>docs/canon/</code> in the website repo, which is the
						source of truth. Edits happen there, never here.
					</p>
				</div>
				{meta && (
					<div className='canon-stamp'>
						<span className='canon-stamp-sha'>{meta.sha}</span>
						<span className='canon-stamp-when'>
							synced {generated ? generated.toLocaleString() : 'unknown'}
						</span>
					</div>
				)}
			</header>

			{error && <p className='canon-error'>Could not load canon: {error}</p>}

			<div className='canon-body'>
				<nav className='canon-list' aria-label='Canon documents'>
					{loading && <p className='canon-muted'>Loading…</p>}
					{!loading &&
						paths.map((p) => (
							<button
								key={p}
								type='button'
								className={`canon-list-item${p === selected ? ' is-active' : ''}`}
								onClick={() => setSelected(p)}>
								{p}
							</button>
						))}
					{!loading && paths.length === 0 && (
						<p className='canon-muted'>
							No canon documents cached. Run <code>node scripts/sync-canon.js --write</code>.
						</p>
					)}
				</nav>

				<article className='canon-doc'>
					{bodyLoading && <p className='canon-muted'>Loading {selected}…</p>}
					{!bodyLoading && !docBody && (
						<p className='canon-muted'>Nothing cached at {selected}.</p>
					)}
					{!bodyLoading && docBody && (
						<>
							<div className='canon-doc-meta'>
								{docBody.path} · {Math.round(docBody.bytes / 1000)} KB · {docBody.lines} lines
							</div>
							{/* First-party content from a write-closed collection — see the
							    trust-boundary note at the top of this file. */}
							<div
								className='canon-md'
								dangerouslySetInnerHTML={{ __html: html }}
							/>
						</>
					)}
				</article>
			</div>
		</div>
	);
};

export default Canon;
